[
  {
    "id": "account-owner-fanout",
    "version": "v0.2.0",
    "title": "020 - Account Owner Fanout CTF",
    "area": "warehouse-modeling-and-metrics",
    "mode": "browser-sql",
    "difficulty": "beginner",
    "estimated_minutes": 30,
    "prerequisites": [
      "first-banking-dataset"
    ],
    "business_scenario": "Prove how a naive balance-to-owner join overstates latest synthetic deposit balances, then return a reconciled one-row result that keeps account-date grain intact.",
    "regulatory_context": [
      "BNR",
      "FGDB",
      "GDPR"
    ],
    "required_tools": "none",
    "inputs": [
      {
        "id": "deposits_seed",
        "type": "dataset",
        "description": "Static synthetic deposits dataset with daily balance snapshots and many-to-many account ownership rows.",
        "dataset_id": "deposits-seed",
        "dataset_version": "v0.1.0",
        "tables": [
          "accounts",
          "account_owners",
          "account_daily_balances"
        ],
        "sensitive_fields": [
          "customer_id",
          "account_id",
          "synthetic_iban"
        ],
        "date_semantics": [
          "business_date is a daily balance snapshot date.",
          "Use the latest available business_date for the fanout proof."
        ],
        "grain": "The safe metric grain is one row per account on the latest business_date before joining or summarizing owner relationships."
      }
    ],
    "outputs": [
      {
        "id": "fanout_query",
        "type": "sql-text",
        "description": "SQL that compares correct account-grain balance totals with naive owner-joined totals."
      },
      {
        "id": "fanout_result",
        "type": "sql-result",
        "description": "One-row result showing the latest date, correct total, naive total, fanout delta, and percentage overstatement."
      }
    ],
    "checks": [
      {
        "id": "required_columns",
        "type": "required-column",
        "description": "Result includes the fanout proof columns.",
        "target": "fanout_result",
        "expected": [
          "latest_balance_date",
          "correct_ledger_total",
          "naive_joined_total",
          "fanout_delta",
          "overstatement_pct"
        ]
      },
      {
        "id": "no_sensitive_fields",
        "type": "sensitive-field-exclusion",
        "description": "Result must not expose sensitive serving-output fields.",
        "target": "fanout_result",
        "expected": [
          "customer_id",
          "account_id",
          "synthetic_iban"
        ]
      },
      {
        "id": "one_fanout_row",
        "type": "row-count",
        "description": "Result returns exactly one fanout proof row.",
        "target": "fanout_result",
        "expected": 1
      },
      {
        "id": "unique_latest_date",
        "type": "unique-key",
        "description": "Result has one unique latest balance date.",
        "target": "fanout_result",
        "expected": [
          "latest_balance_date"
        ]
      },
      {
        "id": "correct_latest_total",
        "type": "scalar-aggregate",
        "description": "Correct latest-date ledger balance total stays at account grain.",
        "target": "fanout_result",
        "expected": {
          "column": "correct_ledger_total",
          "value": 95700,
          "tolerance": 0
        }
      },
      {
        "id": "naive_join_total",
        "type": "scalar-aggregate",
        "description": "Naive owner-joined total demonstrates the fanout overstatement.",
        "target": "fanout_result",
        "expected": {
          "column": "naive_joined_total",
          "value": 164800,
          "tolerance": 0
        }
      },
      {
        "id": "fanout_delta",
        "type": "scalar-aggregate",
        "description": "Fanout delta reconciles the naive total back to the correct total.",
        "target": "fanout_result",
        "expected": {
          "column": "fanout_delta",
          "value": 69100,
          "tolerance": 0
        }
      },
      {
        "id": "overstatement_pct",
        "type": "scalar-aggregate",
        "description": "Fanout percentage is rounded to two decimals.",
        "target": "fanout_result",
        "expected": {
          "column": "overstatement_pct",
          "value": 72.2,
          "tolerance": 0.01
        }
      }
    ],
    "questions": [
      {
        "id": "q_safe_grain",
        "type": "multiple-choice",
        "prompt": "Which grain should the correct latest balance total preserve before owner analysis?",
        "options": [
          {
            "id": "account_latest_date",
            "label": "One row per account on the latest business date."
          },
          {
            "id": "owner_latest_date",
            "label": "One row per account owner on the latest business date."
          },
          {
            "id": "branch_all_dates",
            "label": "One row per branch across all dates."
          }
        ],
        "answer": "account_latest_date",
        "explanation": "Deposit-guarantee ownership analysis must be deliberate; balance snapshots must first stay at account-latest-date grain.",
        "source_facts": [
          "FACT-FGDB-100K-PER-DEPOSITOR-PER-BANK"
        ]
      },
      {
        "id": "q_delta",
        "type": "numeric",
        "prompt": "How many RON/EUR ledger units does the naive owner join overstate on the latest date?",
        "answer": 69100,
        "tolerance": 0,
        "explanation": "The owner join can duplicate balance facts; the latest-date correct total is 95700 and the naive owner-joined total is 164800.",
        "source_facts": [
          "FACT-BI-FANOUT-JOIN-RISK"
        ]
      },
      {
        "id": "q_deposit_guarantee_grain",
        "type": "multiple-choice",
        "prompt": "Which grain does a future per-depositor, per-bank deposit-guarantee coverage metric require?",
        "options": [
          {
            "id": "depositor_bank",
            "label": "One depositor per bank, after resolving ownership."
          },
          {
            "id": "account_balance",
            "label": "One account balance row per business date."
          },
          {
            "id": "owner_joined_balance",
            "label": "One row per account-owner join result."
          }
        ],
        "answer": "depositor_bank",
        "explanation": "The coverage ceiling is per depositor per bank, so account-owner join rows are not the final coverage grain.",
        "source_facts": [
          "FACT-FGDB-100K-PER-DEPOSITOR-PER-BANK"
        ]
      }
    ],
    "lesson_steps": [
      {
        "id": "run_naive_join",
        "title": "Run the naive owner join mentally before trusting it",
        "instruction": "Inspect account_owners and account_daily_balances, then compare the owner-joined total with the account-grain total in one SQL result.",
        "expected_result": "The naive owner-joined total is 164800 while the correct latest account-grain total is 95700.",
        "why_it_matters": "Many-to-many ownership rows can multiply semi-additive balance facts.",
        "failure_mode": "Summing ledger_balance after joining account_owners treats each owner row as another balance.",
        "source_facts": [
          "FACT-BI-FANOUT-JOIN-RISK"
        ]
      },
      {
        "id": "preserve_latest_account_grain",
        "title": "Preserve latest account-date grain first",
        "instruction": "Filter account_daily_balances to the latest business_date and calculate the correct total before joining ownership rows.",
        "expected_result": "correct_ledger_total remains 95700.",
        "why_it_matters": "Snapshot balances are safe to sum across accounts for one date, not after a join that changes row count.",
        "failure_mode": "Grouping after the owner join hides the duplication inside plausible-looking totals.",
        "source_facts": [
          "FACT-FGDB-100K-PER-DEPOSITOR-PER-BANK",
          "FACT-BI-SEMI-ADDITIVE-BALANCE-SNAPSHOT"
        ]
      },
      {
        "id": "reconcile_fanout_delta",
        "title": "Reconcile the fanout delta",
        "instruction": "Return latest_balance_date, correct_ledger_total, naive_joined_total, fanout_delta, and overstatement_pct in one row.",
        "expected_result": "fanout_delta is 69100 and overstatement_pct is 72.20.",
        "why_it_matters": "A CTF-style proof should show both the wrong number and the reconciled correct number.",
        "failure_mode": "Reporting only the corrected total misses the teachable evidence of how the error happened.",
        "source_facts": [
          "FACT-BI-FANOUT-JOIN-RISK"
        ]
      },
      {
        "id": "connect_to_serving_views",
        "title": "Connect the fix to serving-view design",
        "instruction": "Note that a dashboard should consume a curated serving view after grain-safe logic is defined upstream.",
        "expected_result": "Your fix can be described as a serving-view rule, not a one-off chart calculation.",
        "why_it_matters": "BigQuery logical views can centralize reusable SQL for dashboard sources.",
        "failure_mode": "Fixing the total only in a chart leaves the same join trap available to the next report.",
        "source_facts": [
          "FACT-BIGQUERY-LOGICAL-VIEW",
          "FACT-BIGQUERY-VIEW-SCOPE"
        ]
      }
    ],
    "flag": {
      "id": "flag-account-owner-fanout",
      "criteria": [
        "SQL checks prove both the correct account-grain total and the naive owner-joined overstatement.",
        "Fanout questions are answered correctly."
      ]
    },
    "hints": [
      {
        "level": 1,
        "text": "Filter account_daily_balances to the latest business_date before calculating either total."
      },
      {
        "level": 2,
        "text": "Calculate the correct total without account_owners, then calculate the naive total with account_owners and compare them in one final row."
      }
    ],
    "next_challenges": [
      "looker-studio-evidence"
    ]
  },
  {
    "id": "deposit-metric-contract",
    "version": "v0.1.0",
    "title": "050 - Deposit Metric Contract Review",
    "area": "warehouse-modeling-and-metrics",
    "mode": "browser-config",
    "difficulty": "intermediate",
    "estimated_minutes": 25,
    "prerequisites": [
      "first-banking-dataset",
      "account-owner-fanout"
    ],
    "business_scenario": "Define a JSON metric contract for latest synthetic deposit balances before a dashboard team builds charts from it.",
    "regulatory_context": [
      "BNR",
      "FGDB",
      "GDPR"
    ],
    "required_tools": "none",
    "inputs": [
      {
        "id": "deposits_seed",
        "type": "dataset",
        "description": "Synthetic deposits seed used to define the metric contract.",
        "dataset_id": "deposits-seed",
        "dataset_version": "v0.1.0",
        "path": "datasets/deposits-seed/v0.1.0",
        "tables": [
          "account_daily_balances",
          "account_owners",
          "accounts"
        ],
        "sensitive_fields": [
          "account_id",
          "customer_id",
          "synthetic_iban"
        ],
        "grain": "One row per account per business_date in account_daily_balances."
      },
      {
        "id": "learner_tasks",
        "type": "markdown",
        "description": "Learner-task instructions for grain, fanout, and Looker Studio metric ownership.",
        "path": "tutorials/learner-tasks/README.md"
      }
    ],
    "outputs": [
      {
        "id": "metric_contract",
        "type": "metric-contract",
        "description": "JSON contract for a reusable latest deposit balance metric."
      }
    ],
    "checks": [
      {
        "id": "contract_required_fields",
        "type": "json-required-fields",
        "description": "Metric contract includes the required governance and serving fields.",
        "target": "metric_contract_json",
        "expected": [
          "metric_id",
          "source_table",
          "grain",
          "measure",
          "aggregation",
          "date_role",
          "selected_fields",
          "excluded_fields",
          "owner"
        ]
      },
      {
        "id": "contract_core_values",
        "type": "json-field-equals",
        "description": "Metric contract names the correct source table, measure, aggregation, and date role.",
        "target": "metric_contract_json",
        "expected": {
          "source_table": "account_daily_balances",
          "measure": "ledger_balance",
          "aggregation": "SUM",
          "date_role": "business_date"
        }
      },
      {
        "id": "selected_fields_include_serving_shape",
        "type": "json-array-includes",
        "description": "Selected fields include the dashboard serving shape.",
        "target": "metric_contract_json",
        "expected": {
          "selected_fields": [
            "business_date",
            "currency_code",
            "ledger_total"
          ]
        }
      },
      {
        "id": "excluded_fields_include_sensitive_identifiers",
        "type": "json-array-includes",
        "description": "Excluded fields explicitly list sensitive identifiers.",
        "target": "metric_contract_json",
        "expected": {
          "excluded_fields": [
            "account_id",
            "customer_id",
            "synthetic_iban"
          ]
        }
      },
      {
        "id": "selected_fields_exclude_sensitive_identifiers",
        "type": "json-array-excludes",
        "description": "Selected serving fields do not expose sensitive identifiers.",
        "target": "metric_contract_json",
        "expected": {
          "selected_fields": [
            "account_id",
            "customer_id",
            "synthetic_iban"
          ]
        }
      }
    ],
    "questions": [
      {
        "id": "q_metric_owner",
        "type": "multiple-choice",
        "prompt": "Where should reusable fanout-safe balance logic live for a shared dashboard?",
        "options": [
          {
            "id": "upstream_or_datasource",
            "label": "Upstream serving SQL or a reusable data-source field."
          },
          {
            "id": "one_chart",
            "label": "Only in one chart-specific calculated field."
          },
          {
            "id": "raw_owner_join",
            "label": "In the raw account-owner join result."
          }
        ],
        "answer": "upstream_or_datasource",
        "explanation": "Shared balance logic should repair grain before joins and should not be hidden in one-off chart fields.",
        "source_facts": [
          "FACT-BIGQUERY-REDUCE-BEFORE-JOIN",
          "FACT-LOOKER-STUDIO-CALCULATED-FIELD-SCOPE"
        ]
      },
      {
        "id": "q_metric_grain",
        "type": "multiple-choice",
        "prompt": "Which principle explains why the contract must state grain before `ledger_total` is trusted?",
        "options": [
          {
            "id": "grain_before_aggregation",
            "label": "Declare row grain before aggregating balances."
          },
          {
            "id": "viewer_credentials",
            "label": "Configure report viewer credentials."
          },
          {
            "id": "cookie_id",
            "label": "Treat cookie identifiers as possible personal data."
          }
        ],
        "answer": "grain_before_aggregation",
        "explanation": "Row grain must be declared before balance aggregates are trusted.",
        "source_facts": [
          "FACT-BI-GRAIN-DECLARE-BEFORE-AGGREGATION"
        ]
      }
    ],
    "lesson_steps": [
      {
        "id": "start_from_grain",
        "title": "Start from the balance snapshot grain",
        "instruction": "Open the dataset-grain notes and copy the account_daily_balances grain into your metric contract.",
        "expected_result": "The contract states that source rows are account-date balance snapshots before aggregation.",
        "why_it_matters": "BI metrics are easier to review when their grain is explicit before joins or charts.",
        "failure_mode": "A contract that says only deposits balance hides whether the metric is account-level, owner-level, or date-level.",
        "source_facts": [
          "FACT-BI-GRAIN-DECLARE-BEFORE-AGGREGATION",
          "FACT-BI-SEMI-ADDITIVE-BALANCE-SNAPSHOT"
        ]
      },
      {
        "id": "define_serving_shape",
        "title": "Define the serving fields",
        "instruction": "Paste JSON that selects business_date, currency_code, and ledger_total as the serving fields for the metric.",
        "expected_result": "The selected_fields array includes business_date, currency_code, and ledger_total.",
        "why_it_matters": "A narrow serving shape lets report authors chart the metric without dragging raw identifiers into the dashboard.",
        "failure_mode": "Selecting account_id or customer_id in a dashboard metric contract creates unnecessary exposure.",
        "source_facts": [
          "FACT-GDPR-DATA-MINIMISATION",
          "FACT-BIGQUERY-SELECT-LIST-NARROWING"
        ]
      },
      {
        "id": "record_exclusions",
        "title": "Record identifier exclusions",
        "instruction": "Add account_id, customer_id, and synthetic_iban to excluded_fields while keeping them out of selected_fields.",
        "expected_result": "The validator passes the exclusion list and rejects the same fields if they appear in selected_fields.",
        "why_it_matters": "A metric contract should make privacy and serving-output boundaries inspectable before report work starts.",
        "failure_mode": "Listing fields as excluded while also selecting them creates a contradictory contract.",
        "source_facts": [
          "FACT-GDPR-PERSONAL-DATA",
          "FACT-GDPR-DATA-MINIMISATION"
        ]
      },
      {
        "id": "set_metric_ownership",
        "title": "Set reusable metric ownership",
        "instruction": "Mark the metric owner as upstream serving SQL or reusable data-source logic rather than a one-chart formula.",
        "expected_result": "Your answer identifies upstream or reusable data-source ownership for the fanout-safe metric.",
        "why_it_matters": "Shared dashboard metrics should not depend on hidden one-off chart calculations.",
        "failure_mode": "Repairing fanout in one chart but not in the reusable source lets later charts reintroduce the same error.",
        "source_facts": [
          "FACT-BIGQUERY-REDUCE-BEFORE-JOIN",
          "FACT-LOOKER-STUDIO-CALCULATED-FIELD-SCOPE"
        ]
      }
    ],
    "evidence": [
      {
        "id": "metric_contract_json",
        "type": "metric-contract-json",
        "label": "Metric contract JSON",
        "description": "Paste a JSON object that defines the latest deposit balance metric contract.",
        "placeholder": "{\n  \"metric_id\": \"latest_ledger_total\",\n  \"source_table\": \"account_daily_balances\",\n  \"grain\": \"business_date + currency_code\",\n  \"measure\": \"ledger_balance\",\n  \"aggregation\": \"SUM\",\n  \"date_role\": \"business_date\",\n  \"selected_fields\": [\"business_date\", \"currency_code\", \"ledger_total\"],\n  \"excluded_fields\": [\"account_id\", \"customer_id\", \"synthetic_iban\"],\n  \"owner\": \"upstream serving SQL or reusable data-source field\"\n}\n",
        "required": true
      }
    ],
    "flag": {
      "id": "flag-deposit-metric-contract",
      "criteria": [
        "Metric contract JSON checks pass locally.",
        "Fact-backed questions are answered correctly."
      ]
    },
    "hints": [
      {
        "level": 1,
        "text": "Treat selected_fields as the serving output, not as every raw field available in the source tables."
      },
      {
        "level": 2,
        "text": "The expected source table is account_daily_balances and the expected measure is ledger_balance."
      }
    ],
    "next_challenges": [
      "looker-studio-evidence"
    ]
  },
  {
    "id": "first-banking-dataset",
    "version": "v0.2.0",
    "title": "010 - First Banking Dataset Inspection",
    "area": "orientation-and-source-data",
    "mode": "browser-sql",
    "difficulty": "beginner",
    "estimated_minutes": 25,
    "prerequisites": [
      "orientation-quiz"
    ],
    "business_scenario": "Inspect row counts, currencies, branch keys, and snapshot dates in the synthetic deposits seed before building governed BI metrics.",
    "regulatory_context": [
      "BNR",
      "FGDB",
      "GDPR"
    ],
    "required_tools": "none",
    "inputs": [
      {
        "id": "deposits_seed",
        "type": "dataset",
        "description": "Static deposits dataset with Romanian branch geography, RON and EUR balances, and ownership rows.",
        "dataset_id": "deposits-seed",
        "dataset_version": "v0.1.0",
        "tables": [
          "branches",
          "accounts",
          "account_owners",
          "account_daily_balances"
        ],
        "sensitive_fields": [
          "customer_id",
          "account_id",
          "synthetic_iban"
        ],
        "date_semantics": [
          "balance_date is a daily snapshot date.",
          "opened_date is an account lifecycle date."
        ],
        "grain": "One row per account per business_date in account_daily_balances."
      }
    ],
    "outputs": [
      {
        "id": "profile_query",
        "type": "sql-text",
        "description": "SQL that profiles rows, currencies, branches, and snapshot dates."
      },
      {
        "id": "profile_result",
        "type": "sql-result",
        "description": "One-row profile result with row count, currency count, branch count, and latest balance date."
      }
    ],
    "checks": [
      {
        "id": "required_columns",
        "type": "required-column",
        "description": "Result includes expected profiling columns.",
        "target": "profile_result",
        "expected": [
          "row_count",
          "currency_count",
          "branch_count",
          "latest_balance_date"
        ]
      },
      {
        "id": "no_sensitive_fields",
        "type": "sensitive-field-exclusion",
        "description": "Result must not expose sensitive serving-output fields.",
        "target": "profile_result",
        "expected": [
          "customer_id",
          "account_id",
          "synthetic_iban"
        ]
      },
      {
        "id": "no_raw_identifiers",
        "type": "forbidden-column",
        "description": "Result must not expose raw account identifiers.",
        "target": "profile_result",
        "expected": [
          "account_id"
        ]
      },
      {
        "id": "one_profile_row",
        "type": "row-count",
        "description": "Result returns one profiling row.",
        "target": "profile_result",
        "expected": 1
      },
      {
        "id": "unique_profile_date",
        "type": "unique-key",
        "description": "Result has one unique latest balance date.",
        "target": "profile_result",
        "expected": [
          "latest_balance_date"
        ]
      },
      {
        "id": "balance_row_total",
        "type": "scalar-aggregate",
        "description": "Result row count matches the seed daily balance rows.",
        "target": "profile_result",
        "expected": {
          "column": "row_count",
          "value": 18,
          "tolerance": 0
        }
      }
    ],
    "questions": [
      {
        "id": "q_balance_grain",
        "type": "multiple-choice",
        "prompt": "After running the profile query, what grain must be preserved for account_daily_balances before producing a one-row BI profile?",
        "options": [
          {
            "id": "account_day",
            "label": "One row per account per balance date."
          },
          {
            "id": "customer_day",
            "label": "One row per customer per balance date."
          },
          {
            "id": "branch_month",
            "label": "One row per branch per month."
          }
        ],
        "answer": "account_day",
        "explanation": "The profile must first preserve the account-date snapshot grain before any totals are trusted.",
        "source_facts": [
          "FACT-BI-GRAIN-DECLARE-BEFORE-AGGREGATION"
        ]
      },
      {
        "id": "q_profile_output_minimisation",
        "type": "select-all",
        "prompt": "Which raw identifiers should stay out of the one-row profile result unless a specific reporting purpose requires them?",
        "options": [
          {
            "id": "account_id",
            "label": "account_id"
          },
          {
            "id": "customer_id",
            "label": "customer_id"
          },
          {
            "id": "branch_count",
            "label": "branch_count"
          },
          {
            "id": "synthetic_iban",
            "label": "synthetic_iban"
          }
        ],
        "answer": [
          "account_id",
          "customer_id",
          "synthetic_iban"
        ],
        "explanation": "The branch count supports the profile objective, while unnecessary account, customer, and IBAN identifiers should stay out.",
        "source_facts": [
          "FACT-GDPR-PERSONAL-DATA",
          "FACT-GDPR-DATA-MINIMISATION"
        ]
      }
    ],
    "lesson_steps": [
      {
        "id": "inspect_schema",
        "title": "Inspect the loaded synthetic tables",
        "instruction": "Open the challenge and read the schema cards for branches, accounts, account_owners, and account_daily_balances before running SQL.",
        "expected_result": "You can identify account_daily_balances as the balance snapshot table and account_owners as the many-to-many ownership table.",
        "why_it_matters": "Knowing table purpose before joining prevents accidental grain changes in BI metrics.",
        "failure_mode": "Starting with an ownership join makes the profile look richer but can multiply balances later.",
        "source_facts": [
          "FACT-BI-GRAIN-DECLARE-BEFORE-AGGREGATION"
        ]
      },
      {
        "id": "run_profile_query",
        "title": "Run the one-row profile query",
        "instruction": "Run the starter SQL and confirm it returns row_count, currency_count, branch_count, and latest_balance_date.",
        "expected_result": "The result has one row, row_count 18, two currencies, three branches, and the latest balance date.",
        "why_it_matters": "A one-row profile creates a control point before any dashboard or serving-view work.",
        "failure_mode": "Grouping by account_id returns many rows and leaks a sensitive identifier.",
        "source_facts": [
          "FACT-BI-GRAIN-DECLARE-BEFORE-AGGREGATION",
          "FACT-GDPR-DATA-MINIMISATION"
        ]
      },
      {
        "id": "confirm_minimised_output",
        "title": "Confirm the output is minimised",
        "instruction": "Check that the result columns do not include account_id, customer_id, or synthetic_iban.",
        "expected_result": "Only aggregate/profile columns appear in the output.",
        "why_it_matters": "BI serving outputs should include only fields needed for the stated purpose.",
        "failure_mode": "Including raw IDs in a dashboard profile creates unnecessary re-identification habits.",
        "source_facts": [
          "FACT-GDPR-PERSONAL-DATA",
          "FACT-GDPR-DATA-MINIMISATION"
        ]
      },
      {
        "id": "record_deposit_context",
        "title": "Record the depositor-bank grain warning",
        "instruction": "Note that deposit-guarantee coverage is a depositor-bank concept and is not the same as this account-date balance profile.",
        "expected_result": "Your notes distinguish account-date balance grain from depositor-bank guarantee grain.",
        "why_it_matters": "Later exercises need this distinction to avoid using account totals as guarantee coverage.",
        "failure_mode": "Treating every account balance as a separate guaranteed depositor balance overstates coverage semantics.",
        "source_facts": [
          "FACT-FGDB-100K-PER-DEPOSITOR-PER-BANK"
        ]
      }
    ],
    "flag": {
      "id": "flag-first-banking-dataset",
      "criteria": [
        "SQL checks pass for required columns and forbidden sensitive fields.",
        "Grain question is answered correctly."
      ]
    },
    "hints": [
      {
        "level": 1,
        "text": "Start by counting rows and distinct values without joining ownership rows."
      }
    ],
    "next_challenges": [
      "account-owner-fanout"
    ]
  },
  {
    "id": "lending-month-end-snapshots",
    "version": "v0.1.0",
    "title": "024 - Lending Month-End Snapshots",
    "area": "warehouse-modeling-and-metrics",
    "mode": "browser-sql",
    "difficulty": "intermediate",
    "estimated_minutes": 35,
    "prerequisites": [
      "account-owner-fanout"
    ],
    "business_scenario": "Build a currency-level lending exposure result from synthetic loan snapshots and real-estate collateral data while keeping month-end exposure, valuation freshness, and market-index context separate.",
    "regulatory_context": [
      "EBA",
      "DORA",
      "GDPR"
    ],
    "required_tools": "none",
    "inputs": [
      {
        "id": "lending_month_end",
        "type": "dataset",
        "description": "Static synthetic lending month-end dataset with loan contracts, exposure snapshots, products, branches, real-estate collateral, property location bands, and official Romania HPI observations.",
        "dataset_id": "lending-month-end",
        "dataset_version": "v0.1.0",
        "tables": [
          "branches",
          "loan_products",
          "loan_accounts",
          "loan_monthly_snapshots",
          "collateral",
          "property_locations",
          "property_attributes",
          "property_valuations",
          "market_price_bands",
          "romania_house_price_index_annual"
        ],
        "sensitive_fields": [
          "loan_id",
          "borrower_id",
          "synthetic_contract_ref",
          "property_id"
        ],
        "date_semantics": [
          "as_of_date is the exposure snapshot date for outstanding_principal.",
          "origination_date and maturity_date are loan lifecycle dates, not exposure measurement dates.",
          "valuation_date is a collateral valuation date, not a lending exposure snapshot date.",
          "year in romania_house_price_index_annual is an annual market-index observation period, not a property valuation date."
        ],
        "grain": "One row per loan per as_of_date in loan_monthly_snapshots; reduce to the chosen month-end date before currency-level exposure reporting."
      }
    ],
    "outputs": [
      {
        "id": "exposure_query",
        "type": "sql-text",
        "description": "SQL that returns currency-level latest month-end exposure, naive all-date exposure, and the reconciliation delta."
      },
      {
        "id": "exposure_result",
        "type": "sql-result",
        "description": "Two-row result by currency with latest principal, naive time sum, time-sum delta, non-month-end count, stage 3 principal, and collateral quality control columns."
      }
    ],
    "checks": [
      {
        "id": "required_columns",
        "type": "required-column",
        "description": "Result includes all month-end exposure control columns.",
        "target": "exposure_result",
        "expected": [
          "latest_as_of_date",
          "currency_code",
          "latest_principal_total",
          "naive_time_sum_total",
          "time_sum_delta",
          "non_month_end_snapshot_count",
          "stage3_principal_total",
          "latest_property_valuation_total_eur",
          "stale_collateral_valuation_count",
          "latest_romania_hpi_2015_100"
        ]
      },
      {
        "id": "no_sensitive_fields",
        "type": "sensitive-field-exclusion",
        "description": "Result must not expose loan-level or borrower-level identifiers.",
        "target": "exposure_result",
        "expected": [
          "loan_id",
          "borrower_id",
          "synthetic_contract_ref",
          "property_id"
        ]
      },
      {
        "id": "two_currency_rows",
        "type": "row-count",
        "description": "Result returns exactly one row for each currency in the latest month-end snapshot.",
        "target": "exposure_result",
        "expected": 2
      },
      {
        "id": "unique_currency",
        "type": "unique-key",
        "description": "Result has one row per currency code.",
        "target": "exposure_result",
        "expected": [
          "currency_code"
        ]
      },
      {
        "id": "latest_principal_sum",
        "type": "aggregate-total",
        "description": "Latest month-end principal totals reconcile across both currencies.",
        "target": "exposure_result",
        "expected": {
          "column": "latest_principal_total",
          "value": 451000,
          "tolerance": 0
        }
      },
      {
        "id": "time_sum_delta_total",
        "type": "aggregate-total",
        "description": "Naive all-date summing overstates latest exposure by the known time-sum delta.",
        "target": "exposure_result",
        "expected": {
          "column": "time_sum_delta",
          "value": 576000,
          "tolerance": 0
        }
      }
    ],
    "questions": [
      {
        "id": "q_semi_additive_balance",
        "type": "multiple-choice",
        "prompt": "Why should outstanding_principal be filtered to the selected month-end before a currency-level exposure total is reported?",
        "options": [
          {
            "id": "latest_period_not_time_sum",
            "label": "It is a balance snapshot that is additive across loans at one date, not across multiple dates."
          },
          {
            "id": "every_date_activity",
            "label": "It is an activity amount that must be summed across every date to measure exposure."
          },
          {
            "id": "lifecycle_date_only",
            "label": "It is only tied to origination_date and maturity_date, so as_of_date can be ignored."
          }
        ],
        "answer": "latest_period_not_time_sum",
        "explanation": "Balance snapshots can be summed across accounts or loans for one date, but not across time.",
        "source_facts": [
          "FACT-BI-SEMI-ADDITIVE-BALANCE-SNAPSHOT"
        ]
      },
      {
        "id": "q_non_month_end_count",
        "type": "numeric",
        "prompt": "How many loan_monthly_snapshots rows have an as_of_date that is not one of the two declared month-end dates?",
        "answer": 1,
        "tolerance": 0,
        "explanation": "A month-end date check detects the one synthetic non-month-end snapshot row.",
        "source_facts": [
          "FACT-BIGQUERY-LAST-DAY-MONTH-END"
        ]
      },
      {
        "id": "q_ron_time_sum_delta",
        "type": "numeric",
        "prompt": "For RON, what is the difference between summing all outstanding_principal snapshot rows and the 2026-03-31 latest month-end principal total?",
        "answer": 519000,
        "tolerance": 0,
        "explanation": "The reference date and reporting grain must be explicit before aggregation.",
        "source_facts": [
          "FACT-BI-REFERENCE-DATE-SEPARATION",
          "FACT-BI-GRAIN-DECLARE-BEFORE-AGGREGATION"
        ]
      },
      {
        "id": "q_output_minimisation",
        "type": "select-all",
        "prompt": "Which fields from the lending pack should stay out of this currency-level result unless a specific reporting purpose requires them?",
        "options": [
          {
            "id": "loan_id",
            "label": "loan_id"
          },
          {
            "id": "borrower_id",
            "label": "borrower_id"
          },
          {
            "id": "currency_code",
            "label": "currency_code"
          },
          {
            "id": "synthetic_contract_ref",
            "label": "synthetic_contract_ref"
          },
          {
            "id": "property_id",
            "label": "property_id"
          }
        ],
        "answer": [
          "loan_id",
          "borrower_id",
          "synthetic_contract_ref",
          "property_id"
        ],
        "explanation": "The result should return only fields needed for the stated BI purpose.",
        "source_facts": [
          "FACT-GDPR-DATA-MINIMISATION"
        ]
      },
      {
        "id": "q_stale_collateral_count",
        "type": "numeric",
        "prompt": "How many pledged collateral rows use the stale valuation date 2025-03-31 instead of the latest 2026-03-31 valuation date?",
        "answer": 3,
        "tolerance": 0,
        "explanation": "Collateral valuation dates must be managed separately from loan exposure snapshot dates.",
        "source_facts": [
          "FACT-REAL-ESTATE-VALUATION-DATE-SEPARATION"
        ]
      },
      {
        "id": "q_latest_property_valuation_total",
        "type": "numeric",
        "prompt": "What is the deterministic total market_value_eur for property_valuations at valuation_date 2026-03-31?",
        "answer": 11801400,
        "tolerance": 0,
        "explanation": "Property valuation totals must be calculated at property valuation-date grain before joining to loan exposure.",
        "source_facts": [
          "FACT-REAL-ESTATE-COLLATERAL-GRAIN"
        ]
      },
      {
        "id": "q_notarial_minimum_reference",
        "type": "multiple-choice",
        "prompt": "What does the Romanian notarial-study boundary imply for a mortgage BI dashboard?",
        "options": [
          {
            "id": "reference_not_appraisal",
            "label": "They can provide minimum reference-value context, but they are not individual market valuations."
          },
          {
            "id": "direct_appraisal",
            "label": "They are sufficient as property-level mortgage appraisals."
          },
          {
            "id": "borrower_identity",
            "label": "They identify the borrower and exact pledged property."
          }
        ],
        "answer": "reference_not_appraisal",
        "explanation": "Romanian notarial-study values are minimum reference values for specified legal or tax workflows, not market circulation values or loan-level appraisal outputs.",
        "source_facts": [
          "FACT-ROMANIA-NOTARIAL-STUDIES-NOT-MARKET-VALUE"
        ]
      }
    ],
    "lesson_steps": [
      {
        "id": "identify_snapshot_grain",
        "title": "Identify the exposure snapshot grain",
        "instruction": "Open the schema browser and confirm that loan_monthly_snapshots is one row per loan per as_of_date, while loan_accounts has lifecycle dates and loan identifiers.",
        "expected_result": "You can name as_of_date as the exposure measurement date and distinguish it from origination_date, maturity_date, and valuation_date.",
        "why_it_matters": "Dashboard exposure totals need a measurement date, not a mixture of lifecycle, valuation, and refresh dates.",
        "failure_mode": "Using origination_date or maturity_date as the exposure date produces plausible labels but wrong balances.",
        "source_facts": [
          "FACT-BI-REFERENCE-DATE-SEPARATION",
          "FACT-BI-GRAIN-DECLARE-BEFORE-AGGREGATION"
        ]
      },
      {
        "id": "isolate_latest_month_end",
        "title": "Isolate the latest month-end rows",
        "instruction": "Filter loan_monthly_snapshots to as_of_date = '2026-03-31' before calculating latest_principal_total by currency_code.",
        "expected_result": "Latest principal totals are 55000 for EUR and 396000 for RON.",
        "why_it_matters": "Semi-additive balances can be summed across loans at a single selected date.",
        "failure_mode": "Summing every snapshot date reports historical row accumulation as if it were current exposure.",
        "source_facts": [
          "FACT-BI-SEMI-ADDITIVE-BALANCE-SNAPSHOT",
          "FACT-BIGQUERY-LAST-DAY-MONTH-END"
        ]
      },
      {
        "id": "calculate_reconciliation_delta",
        "title": "Calculate the time-sum delta",
        "instruction": "Separately sum outstanding_principal across all snapshot rows by currency_code, then subtract the latest month-end total from the naive all-date total.",
        "expected_result": "The RON time_sum_delta is 519000 and the combined time_sum_delta is 576000.",
        "why_it_matters": "The reconciliation delta makes the semi-additive error mechanically visible.",
        "failure_mode": "Returning only latest_principal_total hides the magnitude of the all-date summing error.",
        "source_facts": [
          "FACT-BI-SEMI-ADDITIVE-BALANCE-SNAPSHOT",
          "FACT-BI-RECONCILIATION-WINDOWS"
        ]
      },
      {
        "id": "count_non_month_end_rows",
        "title": "Count non-month-end snapshots",
        "instruction": "Count rows where as_of_date is not '2026-02-28' and not '2026-03-31', and return that count on each currency result row.",
        "expected_result": "non_month_end_snapshot_count is 1.",
        "why_it_matters": "Date quality checks catch rows that do not belong in month-end exposure reporting.",
        "failure_mode": "Treating every as_of_date as month-end lets a mid-month row contaminate month-end comparisons.",
        "source_facts": [
          "FACT-BIGQUERY-LAST-DAY-MONTH-END"
        ]
      },
      {
        "id": "minimise_serving_output",
        "title": "Minimise the serving output",
        "instruction": "Return only currency-level aggregate columns and exclude loan_id, borrower_id, and synthetic_contract_ref from the result.",
        "expected_result": "The result passes the sensitive-field exclusion check while keeping currency_code and aggregate metrics.",
        "why_it_matters": "A dashboard-ready metric result should not carry unnecessary loan-level identifiers.",
        "failure_mode": "Selecting raw identifiers alongside aggregates encourages accidental detail-level exposure in BI tools.",
        "source_facts": [
          "FACT-GDPR-DATA-MINIMISATION"
        ]
      },
      {
        "id": "add_collateral_quality_controls",
        "title": "Add collateral valuation controls",
        "instruction": "Build CTEs that join the collateral, property_valuations, and romania_house_price_index_annual tables only after calculating property-level latest valuation controls and stale collateral counts.",
        "expected_result": "latest_property_valuation_total_eur is 11801400, stale_collateral_valuation_count is 3, and latest_romania_hpi_2015_100 is 164.82.",
        "why_it_matters": "Mortgage BI work needs valuation freshness and market-index context without pretending that index values are property appraisals.",
        "failure_mode": "Joining every valuation date to every exposure row multiplies collateral values and confuses valuation_date with as_of_date.",
        "source_facts": [
          "FACT-REAL-ESTATE-COLLATERAL-GRAIN",
          "FACT-REAL-ESTATE-VALUATION-DATE-SEPARATION",
          "FACT-EUROSTAT-HPI-MARKET-PRICE-INDEX"
        ]
      }
    ],
    "flag": {
      "id": "flag-lending-month-end-snapshots",
      "criteria": [
        "SQL checks pass for month-end exposure totals, time-sum deltas, and sensitive-field exclusion.",
        "Fact-backed snapshot, date, and minimisation questions are answered correctly."
      ]
    },
    "hints": [
      {
        "level": 1,
        "text": "Start with a latest_snapshots CTE that filters as_of_date to 2026-03-31."
      },
      {
        "level": 2,
        "text": "Keep naive all-date totals in a separate CTE so the final result can show the difference."
      }
    ],
    "next_challenges": [
      "looker-studio-evidence"
    ]
  },
  {
    "id": "looker-studio-evidence",
    "version": "v0.2.0",
    "title": "030 - Looker Studio Evidence Pattern",
    "area": "looker-studio-and-dashboard-design",
    "mode": "cloud-evidence",
    "difficulty": "intermediate",
    "estimated_minutes": 35,
    "prerequisites": [
      "first-banking-dataset"
    ],
    "business_scenario": "Practice a local evidence-pattern simulation for a browser-based synthetic BI workflow without storing credentials. A live Looker Studio report is optional and must be reviewed in the governed Google interface.",
    "regulatory_context": [
      "BNR",
      "GDPR"
    ],
    "required_tools": [
      {
        "name": "Google Cloud Console",
        "purpose": "Optional cloud-applied track for BigQuery UI work in a browser.",
        "required": false,
        "platform_notes": "Browser UI only; no Google Cloud CLI."
      },
      {
        "name": "Looker Studio",
        "purpose": "Optional report-building evidence through the browser UI.",
        "required": false,
        "platform_notes": "Browser UI only; no service account keys or app-stored credentials."
      }
    ],
    "inputs": [
      {
        "id": "serving_view_contract",
        "type": "markdown",
        "description": "Tutorial instructions for using a serving view as the dashboard source.",
        "path": "tutorials/data-sources.md"
      },
      {
        "id": "cloud_ui",
        "type": "cloud-ui",
        "description": "Optional browser UI workflow in BigQuery and Looker Studio; the local app validates evidence format, not live cloud state."
      }
    ],
    "outputs": [
      {
        "id": "serving_sql",
        "type": "sql-text",
        "description": "SQL text for the dashboard serving view."
      },
      {
        "id": "control_result",
        "type": "dashboard-evidence",
        "description": "Pasted CSV or JSON result from the serving view control query."
      },
      {
        "id": "report_url",
        "type": "dashboard-evidence",
        "description": "Looker Studio report URL format evidence."
      }
    ],
    "checks": [
      {
        "id": "serving_sql_names_view",
        "type": "sql-text-contains",
        "description": "SQL text names the serving view instead of only an ad hoc dashboard source.",
        "target": "serving_sql",
        "expected": [
          "serving_deposit_dashboard",
          "account_daily_balances"
        ]
      },
      {
        "id": "pasted_result_columns",
        "type": "tabular-required-columns",
        "description": "Pasted CSV or JSON result includes the expected dashboard control columns.",
        "target": "control_result",
        "expected": [
          "business_date",
          "currency_code",
          "ledger_total"
        ]
      },
      {
        "id": "dashboard_total_range",
        "type": "numeric-range",
        "description": "Visible dashboard total is in the expected synthetic latest-day range.",
        "target": "visible_total",
        "expected": {
          "min": 95000,
          "max": 96000
        }
      },
      {
        "id": "report_url_format",
        "type": "report-url-format",
        "description": "Report URL has a Looker Studio host and uses HTTPS.",
        "target": "report_url",
        "expected": {
          "require_https": true,
          "allowed_hosts": [
            "lookerstudio.google.com",
            "datastudio.google.com"
          ]
        }
      },
      {
        "id": "manual_dashboard_confirmation",
        "type": "checklist-confirmed",
        "description": "Self-attestation that the dashboard visibly labels source freshness and excludes sensitive fields if the optional report-building recipe is completed.",
        "target": "dashboard_checklist",
        "expected": true
      }
    ],
    "questions": [
      {
        "id": "q_credentials",
        "type": "multiple-choice",
        "prompt": "What should be stored as evidence for an optional Looker Studio report review?",
        "options": [
          {
            "id": "no_credentials",
            "label": "No cloud credentials or tokens."
          },
          {
            "id": "service_key",
            "label": "A service account key."
          },
          {
            "id": "oauth_token",
            "label": "A pasted OAuth access token."
          }
        ],
        "answer": "no_credentials",
        "explanation": "Credentials belong in Looker Studio data-source configuration. Review evidence should be limited to inspectable text, output, URLs, or checklist confirmations.",
        "source_facts": [
          "FACT-LOOKER-STUDIO-CREDENTIALS"
        ]
      },
      {
        "id": "q_calculated_field_scope",
        "type": "multiple-choice",
        "prompt": "Where is reusable dashboard metric logic safer than a single-chart field?",
        "options": [
          {
            "id": "data_source_or_upstream",
            "label": "In the shared data source or upstream serving view."
          },
          {
            "id": "single_chart",
            "label": "Only inside one chart-specific calculated field."
          },
          {
            "id": "browser_cookie",
            "label": "In a browser cookie attached to report viewers."
          }
        ],
        "answer": "data_source_or_upstream",
        "explanation": "Governed metrics should be reusable through serving SQL or data-source fields rather than hidden in one chart.",
        "source_facts": [
          "FACT-LOOKER-STUDIO-CALCULATED-FIELD-SCOPE",
          "FACT-BIGQUERY-VIEW-SCOPE"
        ]
      }
    ],
    "lesson_steps": [
      {
        "id": "define_serving_view",
        "title": "Define the serving view SQL",
        "instruction": "Paste SQL that creates or selects a serving_deposit_dashboard view using account_daily_balances and returning business_date, currency_code, and ledger_total.",
        "expected_result": "The SQL text contains serving_deposit_dashboard, account_daily_balances, business_date, currency_code, and ledger_total.",
        "why_it_matters": "A logical view is a reusable SQL-defined source for dashboard charts.",
        "failure_mode": "Building charts from ad hoc raw table fields spreads metric logic across the report.",
        "source_facts": [
          "FACT-BIGQUERY-LOGICAL-VIEW",
          "FACT-BIGQUERY-VIEW-SCOPE"
        ]
      },
      {
        "id": "check_view_limitations",
        "title": "Check view constraints before treating it as dynamic input",
        "instruction": "Confirm that a BigQuery logical view is read-only, same-location with referenced tables, and cannot use query parameters.",
        "expected_result": "Your evidence note does not rely on runtime view parameters or cross-location source tables.",
        "why_it_matters": "Cloud-applied BI instructions should teach operational constraints, not just happy-path SQL.",
        "failure_mode": "Designing a dashboard that assumes parameterized logical views creates an implementation mismatch.",
        "source_facts": [
          "FACT-BIGQUERY-VIEW-LIMITATIONS"
        ]
      },
      {
        "id": "inspect_looker_data_source",
        "title": "Inspect the Looker Studio data source",
        "instruction": "In the optional browser UI track, connect Looker Studio to the serving view and inspect the data source field schema before building charts. If you stay in the browser-only path, record this as an evidence-pattern simulation instead of a completed live report.",
        "expected_result": "The data source or simulated evidence pattern exposes business_date, currency_code, and ledger_total as the chart field schema.",
        "why_it_matters": "Looker Studio reports consume fields through a data source, so field type and credential checks happen there.",
        "failure_mode": "Dragging charts without inspecting the data source can hide wrong field types or unsafe credentials.",
        "source_facts": [
          "FACT-LOOKER-STUDIO-DATA-SOURCE",
          "FACT-LOOKER-STUDIO-CREDENTIALS"
        ]
      },
      {
        "id": "keep_evidence_local",
        "title": "Keep evidence local and credential-free",
        "instruction": "Paste only inspectable SQL, small CSV/JSON control output, a report URL format, and the checklist confirmation; do not paste tokens, keys, credentials, or real banking data.",
        "expected_result": "The app validates local evidence shape and never receives cloud credentials.",
        "why_it_matters": "The static app can verify evidence text without becoming a credential or backend evidence store.",
        "failure_mode": "Pasting service keys or OAuth tokens into a static training app would violate the frontend-only boundary.",
        "source_facts": [
          "FACT-LOOKER-STUDIO-CREDENTIALS",
          "FACT-GDPR-SECURITY-PROCESSING"
        ]
      }
    ],
    "evidence": [
      {
        "id": "serving_sql",
        "type": "pasted-sql",
        "label": "Serving view SQL",
        "description": "Paste the SQL that creates or selects from the dashboard serving view.",
        "placeholder": "CREATE OR REPLACE VIEW serving_deposit_dashboard AS ...",
        "required": true
      },
      {
        "id": "control_result",
        "type": "pasted-result",
        "label": "Pasted control result",
        "description": "Paste CSV or JSON with business_date, currency_code, and ledger_total columns.",
        "placeholder": "business_date,currency_code,ledger_total\n2026-03-31,RON,79300",
        "required": true
      },
      {
        "id": "visible_total",
        "type": "numeric-value",
        "label": "Visible latest-day total",
        "description": "Enter the latest-day dashboard total across currencies from the visible report.",
        "placeholder": "95700",
        "required": true
      },
      {
        "id": "report_url",
        "type": "report-url",
        "label": "Report URL",
        "description": "Paste the Looker Studio report URL for format validation only.",
        "placeholder": "https://lookerstudio.google.com/reporting/example",
        "required": true
      },
      {
        "id": "dashboard_checklist",
        "type": "checklist-confirmation",
        "label": "Dashboard confirmation",
        "description": "Self-attest that the optional live report or browser-only evidence pattern visibly labels source freshness and does not expose account_id, customer_id, or synthetic_iban.",
        "required": true,
        "self_attested": true
      }
    ],
    "flag": {
      "id": "flag-looker-studio-evidence",
      "criteria": [
        "SQL, pasted result, numeric evidence, and URL format checks pass locally.",
        "Credential storage question is answered correctly.",
        "Dashboard freshness and sensitive-field exclusion are self-attested by checklist; this is format evidence unless the learner also completes the optional Looker Studio recipe."
      ]
    },
    "hints": [
      {
        "level": 1,
        "text": "Evidence should describe what is visible without pasting secrets, tokens, or private URLs."
      },
      {
        "level": 2,
        "text": "A valid control result can be pasted as CSV or as a JSON array of objects."
      }
    ]
  },
  {
    "id": "orientation-quiz",
    "version": "v0.2.0",
    "title": "000 - Orientation Quiz",
    "area": "orientation-and-source-data",
    "mode": "quiz",
    "difficulty": "intro",
    "estimated_minutes": 10,
    "prerequisites": [],
    "business_scenario": "Verify the starting BI boundaries before working with synthetic Romanian banking examples.",
    "regulatory_context": [
      "BNR",
      "FGDB",
      "GDPR"
    ],
    "required_tools": "none",
    "inputs": [
      {
        "id": "platform_notes",
        "type": "markdown",
        "description": "Orientation material, regulation notes, and synthetic-data guidance available on this site.",
        "path": "docs/README.md"
      }
    ],
    "outputs": [
      {
        "id": "quiz_answers",
        "type": "answer-set",
        "description": "Browser-local answers to orientation and synthetic-data questions."
      }
    ],
    "checks": [
      {
        "id": "bigquery_view_contract",
        "type": "quiz-answer",
        "description": "Learner identifies BigQuery logical views as SQL-defined virtual tables.",
        "target": "q_bigquery_logical_view",
        "expected": "sql_defined_virtual_table"
      },
      {
        "id": "looker_data_source_contract",
        "type": "quiz-answer",
        "description": "Learner identifies the role of a Looker Studio data source.",
        "target": "q_looker_data_source",
        "expected": "conduit_schema"
      },
      {
        "id": "sensitive_fields",
        "type": "quiz-answer",
        "description": "Learner identifies fields that should be excluded from serving outputs under the data minimisation checkpoint.",
        "target": "q_data_minimisation",
        "expected": [
          "account_id",
          "customer_id",
          "synthetic_iban"
        ]
      },
      {
        "id": "deposit_guarantee_ceiling",
        "type": "quiz-answer",
        "description": "Learner identifies the EU/FGDB standard deposit guarantee ceiling amount.",
        "target": "q_deposit_guarantee_amount",
        "expected": 100000
      }
    ],
    "lesson_steps": [
      {
        "id": "identify_bigquery_view_contract",
        "title": "Verify the serving-view contract",
        "instruction": "Choose the BigQuery object that would keep tested SQL reusable for a dashboard source.",
        "expected_result": "You choose the SQL-defined virtual table, not a spreadsheet or mutable dashboard staging table.",
        "why_it_matters": "Banking BI dashboards need governed serving models before charts reuse the metrics.",
        "failure_mode": "Treating a chart calculation as the governed source duplicates metric logic across reports.",
        "source_facts": [
          "FACT-BIGQUERY-LOGICAL-VIEW",
          "FACT-BIGQUERY-VIEW-SCOPE"
        ]
      },
      {
        "id": "identify_looker_data_source",
        "title": "Verify the Looker Studio source layer",
        "instruction": "Choose the layer a reviewer should inspect before trusting chart fields and aggregation behavior.",
        "expected_result": "You choose the data source as the conduit and schema layer between external data and charts.",
        "why_it_matters": "Banking dashboards should inspect field definitions, aggregation defaults, and credentials before chart building.",
        "failure_mode": "Building directly from fields without checking the data source can hide wrong aggregation or access behavior.",
        "source_facts": [
          "FACT-LOOKER-STUDIO-DATA-SOURCE",
          "FACT-LOOKER-STUDIO-DIMENSIONS-METRICS"
        ]
      },
      {
        "id": "apply_data_minimisation",
        "title": "Verify data minimisation in an output field list",
        "instruction": "Select the fields that should not appear in a routine branch-level dashboard source.",
        "expected_result": "You select account_id, customer_id, and synthetic_iban.",
        "why_it_matters": "GDPR data minimisation is the fact-backed reason to reject unnecessary identifiers in dashboard-ready results.",
        "failure_mode": "Exposing raw identifiers because the data is synthetic trains the wrong production habit.",
        "source_facts": [
          "FACT-GDPR-PERSONAL-DATA",
          "FACT-GDPR-DATA-MINIMISATION"
        ]
      },
      {
        "id": "identify_deposit_ceiling",
        "title": "Verify the guarantee reference amount",
        "instruction": "Enter the standard EUR amount a dashboard note should use before later depositor-bank grain exercises.",
        "expected_result": "You enter 100000.",
        "why_it_matters": "Deposit-guarantee exercises need depositor-bank grain and precise amount/currency wording.",
        "failure_mode": "Confusing account balance totals with depositor-bank compensation coverage creates wrong BI semantics.",
        "source_facts": [
          "FACT-FGDB-100K-PER-DEPOSITOR-PER-BANK",
          "FACT-DGSD-100K-EU"
        ]
      }
    ],
    "questions": [
      {
        "id": "q_bigquery_logical_view",
        "type": "multiple-choice",
        "prompt": "A BI team needs one governed BigQuery object that stores tested SQL and can be queried by dashboards like a table. Which object fits that need?",
        "options": [
          {
            "id": "sql_defined_virtual_table",
            "label": "A virtual table defined by a SQL query."
          },
          {
            "id": "uploaded_spreadsheet",
            "label": "A spreadsheet uploaded into Looker Studio."
          },
          {
            "id": "mutable_staging_table",
            "label": "A mutable staging table updated by dashboard users."
          }
        ],
        "answer": "sql_defined_virtual_table",
        "explanation": "A BigQuery logical view is a virtual table defined by SQL, which makes it useful for reusable serving-layer logic.",
        "source_facts": [
          "FACT-BIGQUERY-LOGICAL-VIEW"
        ]
      },
      {
        "id": "q_looker_data_source",
        "type": "multiple-choice",
        "prompt": "Before trusting a Looker Studio report, which layer should a BI reviewer inspect for fields, aggregation behavior, and connection settings?",
        "options": [
          {
            "id": "conduit_schema",
            "label": "It connects external data to charts and provides the report field schema."
          },
          {
            "id": "browser_cache",
            "label": "It stores each viewer's dashboard click history as metric data."
          },
          {
            "id": "legal_opinion",
            "label": "It determines whether a banking dashboard is compliant."
          }
        ],
        "answer": "conduit_schema",
        "explanation": "The data source is the conduit and schema layer that report charts use.",
        "source_facts": [
          "FACT-LOOKER-STUDIO-DATA-SOURCE"
        ]
      },
      {
        "id": "q_data_minimisation",
        "type": "select-all",
        "prompt": "A branch-level balance dashboard source is being prepared for routine review. Which fields should be removed unless there is a specific approved purpose?",
        "options": [
          {
            "id": "account_id",
            "label": "account_id"
          },
          {
            "id": "branch_city",
            "label": "branch_city"
          },
          {
            "id": "customer_id",
            "label": "customer_id"
          },
          {
            "id": "synthetic_iban",
            "label": "synthetic_iban"
          }
        ],
        "answer": [
          "account_id",
          "customer_id",
          "synthetic_iban"
        ],
        "explanation": "Unnecessary account, customer, and synthetic IBAN identifiers should stay out of serving outputs for this dashboard purpose.",
        "source_facts": [
          "FACT-GDPR-PERSONAL-DATA",
          "FACT-GDPR-DATA-MINIMISATION"
        ]
      },
      {
        "id": "q_deposit_guarantee_amount",
        "type": "numeric",
        "prompt": "A dashboard annotation references the standard EU deposit-guarantee ceiling before any legal review. What harmonised EUR amount applies per depositor per credit institution?",
        "answer": 100000,
        "tolerance": 0,
        "explanation": "The harmonised EU deposit-guarantee coverage ceiling is EUR 100,000 per depositor per credit institution.",
        "source_facts": [
          "FACT-FGDB-100K-PER-DEPOSITOR-PER-BANK",
          "FACT-DGSD-100K-EU"
        ]
      }
    ],
    "flag": {
      "id": "flag-orientation-quiz",
      "criteria": [
        "Answer all orientation questions correctly."
      ]
    },
    "hints": [
      {
        "level": 1,
        "text": "Check the synthetic-data and browser-first constraints shown on the home page."
      }
    ],
    "next_challenges": [
      "first-banking-dataset"
    ]
  }
]
